import { Box, Divider, IconButton, Typography } from "@mui/material"
import { EditOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { Fragment, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"

import { SalesOrderLinePriceOverrideDialog } from "./SalesOrderLinePriceOverrideDialog"

import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import { UOM_RENDER_PRECISION } from "@/constants/formats"
import { FREIGHT_DESCRIPTION_MAX_LENGTH, FREIGHT_QUOTE_NUMBER_MAX_LENGTH } from "@/screens/CustomerOrders/constants"
import { useSalesOrderPermissions } from "@/screens/CustomerOrders/hooks/useSalesOrderPermissions"
import type { FreightLinePriceFormValues, SalesOrderLogisticsFormValues } from "@/screens/CustomerOrders/schemas"
import { formatNumber } from "@/utils/number"
import { isNil } from "@/utils/utils"

const DEFAULT_FREIGHT_UNIT_LABEL = "Each"
const FREIGHT_UNIT_SHORT_LABEL = "ea"

interface SalesOrderFreightLineProps {
  readOnly?: boolean
}

export const SalesOrderFreightLine: FC<SalesOrderFreightLineProps> = ({ readOnly = false }) => {
  const [isPriceDialogOpen, setIsPriceDialogOpen] = useState(false)
  const { isReadonly: isPermissionReadonly } = useSalesOrderPermissions()
  const isReadonly = isPermissionReadonly || readOnly
  const { control, setValue } = useFormContext<SalesOrderLogisticsFormValues>()
  const freightLine = useWatch({ control, name: "freightLine" })

  if (!freightLine) return null

  const hasPrice = !isNil(freightLine.pricePerUom)
  const total = (freightLine.pricePerUom ?? 0) * (freightLine.quantity ?? 0)

  const handlePriceSubmit = ({ pricePerUom, priceUomId }: FreightLinePriceFormValues) => {
    setValue("freightLine.pricePerUom", pricePerUom, { shouldDirty: true, shouldValidate: true })
    setValue("freightLine.priceUomId", priceUomId, { shouldDirty: true })
    setIsPriceDialogOpen(false)
  }

  return (
    <Fragment>
      <Divider />
      <Box className='flex items-start gap-6 px-4 py-6' data-testid='customer-orders.so-freight-line'>
        <Typography variant='body2' className='flex-1 pt-4 text-right font-bold text-gray-800'>
          Freight Quote
        </Typography>
        <FormTextField
          control={control}
          name='freightLine.description'
          label='Description'
          required
          disabled={isReadonly}
          className='w-72'
          slotProps={{ htmlInput: { maxLength: FREIGHT_DESCRIPTION_MAX_LENGTH } }}
          data-testid='customer-orders.so-freight-line.description-field'
        />
        <FormTextField
          control={control}
          name='freightLine.quoteNumber'
          label='Quote Number'
          disabled={isReadonly}
          className='w-48'
          slotProps={{ htmlInput: { maxLength: FREIGHT_QUOTE_NUMBER_MAX_LENGTH } }}
          data-testid='customer-orders.so-freight-line.quote-number-field'
        />
        <FormNumericField
          control={control}
          name='freightLine.quantity'
          label='Quantity'
          required
          allowNegative={false}
          disabled={isReadonly}
          className='w-48'
          data-testid='customer-orders.so-freight-line.quantity-field'
        />
        <Box className='flex w-56 flex-col items-end gap-1'>
          <Box className='flex items-center gap-1'>
            <Typography
              variant='h6'
              className='font-bold text-gray-700'
              data-testid='customer-orders.so-freight-line.price'
            >
              {hasPrice
                ? `$${formatNumber(freightLine.pricePerUom, UOM_RENDER_PRECISION)} /${DEFAULT_FREIGHT_UNIT_LABEL}`
                : "Not Calculated"}
            </Typography>
            <IconButton
              color='primary'
              size='small'
              disabled={isReadonly}
              onClick={() => setIsPriceDialogOpen(true)}
              data-testid='customer-orders.so-freight-line.edit-price-button'
            >
              <EditOutlined />
            </IconButton>
          </Box>
          {hasPrice && (
            <Typography variant='caption' className='text-gray-700'>
              Total: ${formatNumber(total, UOM_RENDER_PRECISION)}
            </Typography>
          )}
        </Box>

        {isPriceDialogOpen && (
          <SalesOrderLinePriceOverrideDialog
            title='Change Freight Price'
            pricePerUom={freightLine.pricePerUom}
            priceUomId={freightLine.priceUomId ?? null}
            isFreightPrice
            unitLabel={FREIGHT_UNIT_SHORT_LABEL}
            onClose={() => setIsPriceDialogOpen(false)}
            onSubmit={handlePriceSubmit}
          />
        )}
      </Box>
    </Fragment>
  )
}
